/**
 * LanguageContext - Global language preference management
 *
 * Provides language preference throughout the application with:
 * - Persistence to localStorage (immediate)
 * - Persistence to backend user preferences (async)
 * - Translation helper function via API
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authFetch } from '@/utils/auth-fetch';

// Supported languages with metadata
export interface Language {
  code: string;
  name: string;
  native: string;
  flag: string;
}

export const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'en', name: 'English (UK)', native: 'English', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'en-US', name: 'English (US)', native: 'English', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: 'es', name: 'Spanish', native: 'Espa\u00F1ol', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'it', name: 'Italian', native: 'Italiano', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: 'fr', name: 'French', native: 'Fran\u00E7ais', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'de', name: 'German', native: 'Deutsch', flag: '\u{1F1E9}\u{1F1EA}' }
];

// Content types for translation context
export type ContentType =
  | 'sector_research'
  | 'buyer_persona'
  | 'perceived_benefit'
  | 'attribute_mapping'
  | 'category_description'
  | 'question_answer'
  | 'product_description';

interface TranslationResult {
  translated: string;
  language: string;
  cached: boolean;
  source: string;
}

interface LanguageContextType {
  // Current language state
  language: string;
  languageInfo: Language | undefined;
  setLanguage: (lang: string) => void;
  loading: boolean;

  // Available languages
  languages: Language[];

  // Translation helper
  translate: (
    content: string,
    contentType: ContentType,
    contentKey: string
  ) => Promise<TranslationResult>;

  // Batch translation
  translateBatch: (
    items: Array<{ content: string; contentType: ContentType; contentKey: string }>
  ) => Promise<Array<{ content: string; translated: string; cached: boolean }>>;

  // Object field translation
  translateFields: <T extends Record<string, unknown>>(
    obj: T,
    fields: (keyof T)[],
    contentType: ContentType,
    contentKey: string
  ) => Promise<T>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'preferredLanguage';

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguageState] = useState<string>('en');
  const [loading, setLoading] = useState(true);

  // Get language info for current language
  const languageInfo = SUPPORTED_LANGUAGES.find((l) => l.code === language);

  // Load preference from localStorage and backend on mount
  useEffect(() => {
    const loadPreference = async () => {
      setLoading(true);

      // First, try localStorage for immediate feedback
      const storedLang = localStorage.getItem(STORAGE_KEY);
      if (storedLang && SUPPORTED_LANGUAGES.some((l) => l.code === storedLang)) {
        setLanguageState(storedLang);
      }

      // Then try to get from backend (authoritative source)
      try {
        const response = await authFetch('/api/users/me/preferences');
        if (response.ok) {
          const data = await response.json();
          if (data.preferredLanguage && SUPPORTED_LANGUAGES.some((l) => l.code === data.preferredLanguage)) {
            setLanguageState(data.preferredLanguage);
            localStorage.setItem(STORAGE_KEY, data.preferredLanguage);
          }
        }
      } catch (error) {
        console.error('Error loading language preference:', error);
        // Keep localStorage value or default
      }

      setLoading(false);
    };

    loadPreference();
  }, []);

  // Set language with persistence
  const setLanguage = useCallback(async (lang: string) => {
    // Validate language
    if (!SUPPORTED_LANGUAGES.some((l) => l.code === lang)) {
      console.warn(`Invalid language code: ${lang}`);
      return;
    }

    // Update state immediately
    setLanguageState(lang);

    // Persist to localStorage (immediate)
    localStorage.setItem(STORAGE_KEY, lang);

    // Persist to backend (async)
    try {
      await authFetch('/api/users/me/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferredLanguage: lang })
      });
    } catch (error) {
      console.error('Error saving language preference:', error);
      // Don't revert - localStorage will maintain the preference
    }
  }, []);

  // Translate a single piece of content
  const translate = useCallback(
    async (content: string, contentType: ContentType, contentKey: string): Promise<TranslationResult> => {
      // No translation needed for English (UK or US) or if content is empty
      if (language === 'en' || language === 'en-US' || !content) {
        return {
          translated: content,
          language: 'en',
          cached: true,
          source: 'same_language'
        };
      }

      try {
        const response = await authFetch('/api/translations/translate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content,
            contentType,
            contentKey,
            targetLanguage: language,
            sourceLanguage: 'en'
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            return data.translation;
          }
        }

        // Fallback to original
        return {
          translated: content,
          language,
          cached: false,
          source: 'fallback'
        };
      } catch (error) {
        console.error('Translation error:', error);
        return {
          translated: content,
          language,
          cached: false,
          source: 'error'
        };
      }
    },
    [language]
  );

  // Batch translate multiple items
  const translateBatch = useCallback(
    async (
      items: Array<{ content: string; contentType: ContentType; contentKey: string }>
    ): Promise<Array<{ content: string; translated: string; cached: boolean }>> => {
      // No translation needed for English (UK or US)
      if (language === 'en' || language === 'en-US') {
        return items.map((item) => ({
          content: item.content,
          translated: item.content,
          cached: true
        }));
      }

      try {
        const response = await authFetch('/api/translations/translate-batch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items,
            targetLanguage: language
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            return data.translations;
          }
        }

        // Fallback to originals
        return items.map((item) => ({
          content: item.content,
          translated: item.content,
          cached: false
        }));
      } catch (error) {
        console.error('Batch translation error:', error);
        return items.map((item) => ({
          content: item.content,
          translated: item.content,
          cached: false
        }));
      }
    },
    [language]
  );

  // Translate specific fields of an object
  const translateFields = useCallback(
    async <T extends Record<string, unknown>>(
      obj: T,
      fields: (keyof T)[],
      contentType: ContentType,
      contentKey: string
    ): Promise<T> => {
      // No translation needed for English (UK or US)
      if (language === 'en' || language === 'en-US') {
        return obj;
      }

      try {
        const response = await authFetch('/api/translations/translate-object', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            object: obj,
            fields,
            contentType,
            contentKey,
            targetLanguage: language
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            return data.translated as T;
          }
        }

        return obj;
      } catch (error) {
        console.error('Object translation error:', error);
        return obj;
      }
    },
    [language]
  );

  const value: LanguageContextType = {
    language,
    languageInfo,
    setLanguage,
    loading,
    languages: SUPPORTED_LANGUAGES,
    translate,
    translateBatch,
    translateFields
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
